import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Mail, MapPin, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { siteConfig } from '../data/mock';
import { submitContactMessage } from '../services/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await submitContactMessage(formData);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <div className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight size={16} />
            <span>Contact</span>
          </div>
          <h1 className="display-medium">Contact Us</h1>
          <p className="body-large">
            We&apos;d love to hear from you. Get in touch with The Grazing Trust.
          </p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '64px' }}>
            {/* Contact Info */}
            <div>
              <h2 className="heading-2" style={{ marginBottom: '24px' }}>Get in Touch</h2>
              <p className="body-medium" style={{ marginBottom: '32px' }}>
                Whether you have a question, want to get involved, or need help for a horse, we&apos;re here to listen.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div 
                  className="network-card" 
                  style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}
                >
                  <div 
                    style={{ 
                      width: '48px', 
                      height: '48px', 
                      background: 'var(--brand-primary)', 
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Mail size={24} color="var(--brand-dark)" />
                  </div>
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '4px' }}>Email</h3>
                    <p className="body-medium">{siteConfig.email}</p>
                  </div>
                </div>

                <div 
                  className="network-card" 
                  style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}
                >
                  <div 
                    style={{ 
                      width: '48px', 
                      height: '48px', 
                      background: 'var(--brand-primary)', 
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <MapPin size={24} color="var(--brand-dark)" />
                  </div>
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '4px' }}>Location</h3>
                    <p className="body-medium">{siteConfig.address}</p>
                  </div>
                </div>
              </div>

              <div 
                style={{ 
                  background: 'var(--bg-subtle)', 
                  padding: '24px', 
                  borderRadius: '16px',
                  marginTop: '32px'
                }}
              >
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>Quick Links</h3>
                <ul style={{ paddingLeft: '0', listStyle: 'none', margin: 0 }}>
                  <li style={{ marginBottom: '8px' }}>
                    <Link to="/help-a-horse" style={{ color: 'var(--brand-dark)', fontWeight: 500 }}>
                      → Intake Enquiry (Help a Horse)
                    </Link>
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <Link to="/get-involved/volunteer" style={{ color: 'var(--brand-dark)', fontWeight: 500 }}>
                      → Volunteer Application
                    </Link>
                  </li>
                  <li style={{ marginBottom: '8px' }}>
                    <Link to="/livery-scheme" style={{ color: 'var(--brand-dark)', fontWeight: 500 }}>
                      → Livery Yard Membership
                    </Link>
                  </li>
                  <li>
                    <Link to="/donate" style={{ color: 'var(--brand-dark)', fontWeight: 500 }}>
                      → Make a Donation
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="network-card" style={{ padding: '40px' }}>
              {submitted ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <CheckCircle2 size={48} color="#10b981" style={{ marginBottom: '16px' }} />
                  <h2 className="heading-2" style={{ marginBottom: '16px' }}>Message Sent</h2>
                  <p className="body-medium" style={{ marginBottom: '24px' }}>
                    Thank you for getting in touch. We&apos;ll respond as soon as possible.
                  </p>
                  <button 
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: '', email: '', subject: '', message: '' });
                    }}
                    className="btn-secondary"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="heading-2" style={{ marginBottom: '8px' }}>Send a Message</h2>
                  <p className="body-small" style={{ marginBottom: '32px' }}>
                    Fill in the form below and we&apos;ll get back to you.
                  </p>

                  {error && (
                    <div 
                      style={{ 
                        background: '#FEE2E2', 
                        padding: '16px', 
                        borderRadius: '12px', 
                        marginBottom: '24px',
                        color: '#DC2626'
                      }}
                    >
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      <div className="form-group">
                        <Label htmlFor="name">Your Name *</Label>
                        <Input 
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          required
                          style={{ marginTop: '8px' }}
                        />
                      </div>
                      <div className="form-group">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input 
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          required
                          style={{ marginTop: '8px' }}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <Label>Subject *</Label>
                      <Select 
                        value={formData.subject}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, subject: value }))}
                      >
                        <SelectTrigger style={{ marginTop: '8px' }}>
                          <SelectValue placeholder="What is your enquiry about?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General Enquiry</SelectItem>
                          <SelectItem value="donate">Donations</SelectItem>
                          <SelectItem value="volunteer">Volunteering</SelectItem>
                          <SelectItem value="rehoming">Rehoming a Horse</SelectItem>
                          <SelectItem value="livery">Livery Scheme</SelectItem>
                          <SelectItem value="media">Media / Press</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="form-group">
                      <Label htmlFor="message">Your Message *</Label>
                      <Textarea 
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                        placeholder="How can we help?"
                        required
                        style={{ marginTop: '8px', minHeight: '150px' }}
                      />
                    </div>

                    <button 
                      type="submit" 
                      className="btn-primary" 
                      style={{ width: '100%' }}
                      disabled={loading || !formData.subject}
                    >
                      {loading ? (
                        <>
                          <Loader2 size={18} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={18} style={{ marginRight: '8px' }} />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </main>
  );
};

export default ContactPage;
